// =============================================
// Claude LLM Provider
// Wrapper on existing callClaude() pattern from thematicMapService.ts
// =============================================

import type { LLMProvider, LLMCallOptions, LLMResponse } from "./types";
import { getApiTarget } from "@/lib/apiBase";
import { trackLlmCall } from "./costTracker";
import { logDebug } from "../contentEngine/contentLogger";

const DEFAULT_MODEL = "claude-sonnet-5";
const DEFAULT_MAX_TOKENS = 4096;
const DEFAULT_TEMPERATURE = 0.7;
const DEFAULT_TIMEOUT_MS = 300_000; // 5 minutes — content engine needs long responses

const RETRY_MAX_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 2000;

/**
 * Fetch with per-attempt timeout + exponential backoff retry for transient errors.
 *
 * Retriable: 5xx, 429 (rate limit — honors Retry-After), 408 (timeout),
 * AbortError (our timeout), network errors (TypeError from fetch).
 * NON-retriable: 4xx (auth/validation) — thrown immediately.
 *
 * Each attempt gets a fresh AbortController — timeout from attempt N does not
 * kill attempt N+1.
 *
 * Pattern 1:1 z src/services/llm/openaiProvider.ts (HOTFIX 2026-05-27).
 */
async function fetchWithRetry(
  url: string,
  init: Omit<RequestInit, "signal">,
  timeoutMs: number,
  label: string,
): Promise<Response> {
  let lastErr: unknown;

  for (let attempt = 1; attempt <= RETRY_MAX_ATTEMPTS; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const resp = await fetch(url, { ...init, signal: controller.signal });
      clearTimeout(timeoutId);

      const isRetriable =
        resp.status === 408 || resp.status === 429 || resp.status >= 500;

      if (isRetriable && attempt < RETRY_MAX_ATTEMPTS) {
        const retryAfter = resp.headers.get("retry-after");
        const retryAfterMs = retryAfter ? (parseInt(retryAfter, 10) || 0) * 1000 : 0;
        const backoff = RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
        const jitter = Math.floor(Math.random() * 500);
        const delayMs = Math.max(retryAfterMs, backoff + jitter);
        logDebug(
          "Claude",
          `${label} retry ${attempt}/${RETRY_MAX_ATTEMPTS}: HTTP ${resp.status}, czekam ${delayMs}ms`,
        );
        await new Promise((r) => setTimeout(r, delayMs));
        continue;
      }

      return resp;
    } catch (err: unknown) {
      clearTimeout(timeoutId);
      lastErr = err;

      const isAbort = err instanceof Error && err.name === "AbortError";
      const isNetwork = err instanceof TypeError;
      const isRetriable = isAbort || isNetwork;

      if (isRetriable && attempt < RETRY_MAX_ATTEMPTS) {
        const delayMs =
          RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1) + Math.floor(Math.random() * 500);
        const reason = isAbort ? `timeout ${timeoutMs / 1000}s` : "network";
        logDebug(
          "Claude",
          `${label} retry ${attempt}/${RETRY_MAX_ATTEMPTS}: ${reason}, czekam ${delayMs}ms`,
        );
        await new Promise((r) => setTimeout(r, delayMs));
        continue;
      }

      if (isAbort) {
        throw new Error(
          `Claude API timeout — brak odpowiedzi w ciagu ${timeoutMs / 1000}s (po ${attempt} probach).`,
        );
      }
      throw err;
    }
  }

  throw lastErr;
}

/** Model shorthand -> full API ID (used when alias needs resolution) */
const CLAUDE_MODEL_IDS: Record<string, string> = {
  // Claude 5 (2026-08) — aliasy bez daty (dokladne ID z platform.claude.com).
  // NIGDY claude-fable-5 (inna cena, inne API — decyzja wlasciciela).
  "claude-opus-5": "claude-opus-5",
  "claude-sonnet-5": "claude-sonnet-5",
  // v4.8 — legacy Opus
  "claude-opus-4-8": "claude-opus-4-8",
  // v4.6 family — use alias directly (no snapshot date yet)
  "claude-opus-4-6": "claude-opus-4-6",
  "claude-sonnet-4-6": "claude-sonnet-4-6",
  // v4.5 family — snapshot dates for stable pinning
  "claude-sonnet-4-5": "claude-sonnet-4-5-20250929",
  "claude-sonnet-4-0": "claude-sonnet-4-0",
  "claude-haiku-4-5": "claude-haiku-4-5-20251001",
};

/** Approximate pricing per 1M tokens (USD) — source: platform.claude.com (2026-08) */
const CLAUDE_PRICING: Record<string, { input: number; output: number }> = {
  "claude-opus-5":     { input: 5,  output: 25 },    // Opus 5 — ta sama cena co 4.8
  "claude-sonnet-5":   { input: 3,  output: 15 },    // Sonnet 5 (intro $2/$10 do 2026-08-31)
  "claude-opus-4-8":   { input: 5,  output: 25 },    // legacy
  "claude-opus-4-7":   { input: 5,  output: 25 },    // legacy (cennik ujednolicony z 4.8)
  "claude-opus-4-6":   { input: 5,  output: 25 },    // legacy
  "claude-sonnet-4-6": { input: 3,  output: 15 },    // legacy
  "claude-sonnet-4-5": { input: 3,  output: 15 },    // legacy
  "claude-sonnet-4-0": { input: 3,  output: 15 },    // legacy
  "claude-haiku-4-5":  { input: 1,  output: 5 },     // fastest
};

/**
 * Modele BEZ wsparcia dla parametrow probkowania.
 *
 * Zrodlo: platform.claude.com — "Sampling parameters not supported": Opus 4.8,
 * tak jak Opus 4.7, nie przyjmuje `temperature` / `top_p` / `top_k` — ustawienie
 * ich na wartosc inna niz domyslna konczy sie bledem 400. Model sam zarzadza
 * probkowaniem; wariacje wymusza sie trescia promptu, nie parametrem.
 *
 * Analogia do HARD RULE dla reasoning models OpenAI (gpt-5.x bez temperature).
 */
// Rodzina Claude 5 (opus-5/sonnet-5) tez odrzuca sampling params — potwierdzone
// w docs (migration-guide + model-deprecations, Context7 2026-08-23): temperature/
// top_p/top_k na Opus 4.7+ i Sonnet 5 => 400.
const NO_SAMPLING_MODELS = new Set([
  "claude-opus-5",
  "claude-sonnet-5",
  "claude-opus-4-8",
  "claude-opus-4-7",
]);

export function supportsSampling(modelId: string): boolean {
  return !NO_SAMPLING_MODELS.has(modelId);
}

// Claude 5: pominiecie `thinking` = ADAPTIVE THINKING WLACZONE domyslnie,
// a tokeny myslenia licza sie do max_tokens. Nasze wywolania maja male limity
// (4096-16384) dobrane pod modele bez myslenia — Sonnet 5 zuzywal caly limit
// na myslenie i odpowiedz byla ucieta ("4096 z 4096", incydent 2026-08-23).
// Jawne {type:"disabled"} jest wspierane i przywraca DOKLADNIE stare
// zachowanie (Sonnet 4.5/4.6 i Opus 4.8 nie myslaly bez jawnej konfiguracji).
const THINKING_OFF_MODELS = new Set(["claude-opus-5", "claude-sonnet-5"]);

/** Config `thinking` dla requestu — {type:"disabled"} dla Claude 5, undefined dla reszty. */
export function thinkingConfigFor(modelId: string): { type: "disabled" } | undefined {
  return THINKING_OFF_MODELS.has(modelId) ? { type: "disabled" } : undefined;
}

function resolveModelId(model: string): string {
  return CLAUDE_MODEL_IDS[model] || model;
}

function estimateCost(model: string, tokensIn: number, tokensOut: number): number {
  const pricing = CLAUDE_PRICING[model] || CLAUDE_PRICING["claude-sonnet-4-6"]!;
  return (tokensIn * pricing.input + tokensOut * pricing.output) / 1_000_000;
}

export const claudeProvider: LLMProvider = {
  type: "claude",

  async call(prompt: string, options: LLMCallOptions): Promise<LLMResponse> {
    const model = options?.model || DEFAULT_MODEL;
    const modelId = resolveModelId(model);
    const maxTokens = options?.maxTokens || DEFAULT_MAX_TOKENS;
    const temperature = options?.temperature ?? DEFAULT_TEMPERATURE;
    const timeoutMs = options?.timeoutMs || DEFAULT_TIMEOUT_MS;
    const tStart = Date.now();

    const messages: Array<{ role: string; content: string }> = [
      { role: "user", content: prompt },
    ];

    const body: Record<string, unknown> = {
      model: modelId,
      max_tokens: maxTokens,
      messages,
    };

    // temperature TYLKO dla modeli, ktore ja przyjmuja — Opus 4.7/4.8 zwracaja 400.
    // Wartosc z DB (thematic_prompts.temperature) jest wtedy swiadomie ignorowana.
    if (supportsSampling(modelId)) {
      body.temperature = temperature;
    }

    // Claude 5: wylacz domyslne adaptive thinking (tokeny myslenia zjadaly
    // max_tokens -> "Odpowiedz ucieta"). Patrz komentarz przy THINKING_OFF_MODELS.
    const thinking = thinkingConfigFor(modelId);
    if (thinking) {
      body.thinking = thinking;
    }

    // Anthropic API: system prompt goes as top-level "system" field, NOT in messages
    if (options?.systemPrompt) {
      body.system = options.systemPrompt;
    }

    // Note: output_config.json_schema requires specific API versions.
    // Prompts already instruct "Zwróć TYLKO JSON" — schema validation done client-side.
    // If jsonSchema provided, we skip server-side enforcement to avoid 400 errors.

    const target = getApiTarget("anthropic", "/v1/messages");
    const response = await fetchWithRetry(
      target.url,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
          ...target.headers,
        },
        body: JSON.stringify(body),
      },
      timeoutMs,
      `${modelId}/${options?.step || "?"}`,
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Claude API error: ${response.status} - ${errorData.error?.message || "Unknown"}`,
      );
    }

    const result = await response.json();

    if (result.stop_reason === "max_tokens") {
      throw new Error(
        `Odpowiedź ucięta (${result.usage?.output_tokens || "?"} tokenów z limitu ${maxTokens}).`,
      );
    }

    const text = result.content?.[0]?.text?.trim() || "";
    const tokensInput = result.usage?.input_tokens || 0;
    const tokensOutput = result.usage?.output_tokens || 0;
    const cachedTokens = result.usage?.cache_read_input_tokens || 0;
    const durationMs = Date.now() - tStart;
    const costUsd = estimateCost(model, tokensInput, tokensOutput);

    trackLlmCall({
      provider: "claude",
      model,
      step: options?.step,
      tokensInput, tokensOutput, cachedTokens,
      costUsd, durationMs,
      mapId: options?.mapId, nodeId: options?.nodeId, runId: options?.runId,
    });

    return {
      text,
      tokensInput,
      tokensOutput,
      modelUsed: modelId,
      provider: "claude",
      costUsd,
      cachedTokens,
      durationMs,
    };
  },
};
